import type {
  CalendarSyncEventData,
  CalendarSyncPhase,
  CalendarSyncProvider,
  CalendarSyncState,
} from "@/types/socket"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { convertISODateToTimestamp, nowISOString } from "@/lib/date-utils"
import { RootState } from "./redux-store"

export interface CalendarSyncPhaseRecord {
  state: CalendarSyncState
  occurredAt: string
  errorMessage: string | null
  metadata?: CalendarSyncEventData["metadata"]
}

export interface CalendarSyncScopeRecord {
  provider: CalendarSyncProvider
  connectionId: number
  syncSessionId: number | null
  phases: Partial<Record<CalendarSyncPhase, CalendarSyncPhaseRecord>>
  needsReconciliation: boolean
  lastReconciledAt: string | null
}

export interface CalendarSyncSliceState {
  scopes: Record<string, CalendarSyncScopeRecord>
}

export const calendarSyncInitialState: CalendarSyncSliceState = {
  scopes: {},
}

function scopeKeyFor(
  provider: CalendarSyncProvider,
  connectionId: number,
  syncSessionId: number | null
) {
  return `${provider}:${connectionId}:${syncSessionId ?? "none"}`
}

function statePriority(state: CalendarSyncState) {
  switch (state) {
    case "failed":
      return 3
    case "completed":
      return 2
    case "running":
      return 1
    case "queued":
      return 0
  }
}

function eventTimestamp(value: string) {
  const parsed = convertISODateToTimestamp(value).epoch_millis
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
}

function shouldApplyEvent(
  current: CalendarSyncPhaseRecord | undefined,
  incoming: CalendarSyncEventData
) {
  if (!current) return true

  const currentTime = eventTimestamp(current.occurredAt)
  const incomingTime = eventTimestamp(incoming.occurred_at)

  if (incomingTime > currentTime) return true
  if (incomingTime < currentTime) return false

  return statePriority(incoming.state) >= statePriority(current.state)
}

function isTerminalState(state: CalendarSyncState) {
  return state === "completed" || state === "failed"
}

const calendarSyncSlice = createSlice({
  name: "calendarSyncSlice",
  initialState: calendarSyncInitialState,
  reducers: {
    receiveCalendarSyncEvent: (
      state,
      action: PayloadAction<CalendarSyncEventData>
    ) => {
      const event = action.payload
      const scopeKey = scopeKeyFor(
        event.provider,
        event.connection_id,
        event.sync_session_id
      )
      const scope =
        state.scopes[scopeKey] ??
        (state.scopes[scopeKey] = {
          provider: event.provider,
          connectionId: event.connection_id,
          syncSessionId: event.sync_session_id,
          phases: {},
          needsReconciliation: false,
          lastReconciledAt: null,
        })

      const current = scope.phases[event.phase]
      if (!shouldApplyEvent(current, event)) return

      scope.phases[event.phase] = {
        state: event.state,
        occurredAt: event.occurred_at,
        errorMessage: event.error_message,
        metadata: event.metadata,
      }

      if (isTerminalState(event.state)) {
        scope.needsReconciliation = true
      }
    },

    markCalendarSyncScopeReconciled: (
      state,
      action: PayloadAction<{
        provider: CalendarSyncProvider
        connectionId: number
        syncSessionId: number | null
        reconciledAt?: string
      }>
    ) => {
      const { provider, connectionId, syncSessionId, reconciledAt } =
        action.payload
      const scope =
        state.scopes[scopeKeyFor(provider, connectionId, syncSessionId)]
      if (!scope) return

      scope.needsReconciliation = false
      scope.lastReconciledAt = reconciledAt ?? nowISOString()
    },

    markCalendarSyncReconnect: (state) => {
      Object.values(state.scopes).forEach((scope) => {
        scope.needsReconciliation = true
      })
    },

    resetCalendarSyncState: () => calendarSyncInitialState,
  },
})

export const {
  receiveCalendarSyncEvent,
  markCalendarSyncScopeReconciled,
  markCalendarSyncReconnect,
  resetCalendarSyncState,
} = calendarSyncSlice.actions

export function selectCalendarSyncScopes(state: RootState) {
  return state.calendarSyncSlice.scopes
}

export function selectCalendarSyncScope(
  state: RootState,
  args: {
    provider: CalendarSyncProvider
    connectionId: number
    syncSessionId: number | null
  }
) {
  return state.calendarSyncSlice.scopes[
    scopeKeyFor(args.provider, args.connectionId, args.syncSessionId)
  ]
}

export function selectCalendarSyncNeedsReconciliation(state: RootState) {
  return Object.values(state.calendarSyncSlice.scopes).some(
    (scope) => scope.needsReconciliation
  )
}

export function selectCalendarSyncReconciliationScopes(state: RootState) {
  return Object.values(state.calendarSyncSlice.scopes).filter(
    (scope) => scope.needsReconciliation
  )
}

export { scopeKeyFor as calendarSyncScopeKeyFor }
export default calendarSyncSlice
